import { useState, useEffect } from 'react';
import { photosDb, type LocalPhoto } from '@/features/calibrationPhotos';

type WorkOrderDetail = {
  globalId?: string | null;
};

type ValidWorkOrderDetail = {
  globalId: string;
};

type UseLocalPhotosResult = {
  localPhotosMap: Map<string, LocalPhoto[]>;
  isLoaded: boolean;
};

export const useLocalPhotos = (
  jobNumber: string | undefined,
  details: WorkOrderDetail[]
): UseLocalPhotosResult => {
  const [localPhotosMap, setLocalPhotosMap] = useState<
    Map<string, LocalPhoto[]>
  >(new Map());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchLocalPhotos = async () => {
      if (!jobNumber || details.length === 0) {
        setIsLoaded(true);
        return;
      }

      const validDetails = details.filter(
        (d): d is ValidWorkOrderDetail => d.globalId != null
      );

      const results = await Promise.all(
        validDetails.map(async detail => {
          const calibrationId = `${jobNumber}_${detail.globalId}`;
          try {
            const photos =
              await photosDb.getPhotosForCalibration(calibrationId);
            return { globalId: detail.globalId, photos };
          } catch {
            return { globalId: detail.globalId, photos: [] };
          }
        })
      );

      const photoMap = new Map(
        results
          .filter(r => r.photos.length > 0)
          .map(r => [r.globalId, r.photos])
      );

      setLocalPhotosMap(photoMap);
      setIsLoaded(true);
    };

    fetchLocalPhotos();
  }, [details, jobNumber]);

  return { localPhotosMap, isLoaded };
};
